<template>
    <div class="album" :class="showTierBorder ? tierClass(album.tier) : ''" @click="emitAlbumClicked">
        <!-- Test Album Badge -->
        <div v-if="album.is_test_album" class="test-album-badge" title="Test Album">
          🧪
        </div>
        <img :src="album.cover_url" alt="Album cover">
        <p class="artist">{{ album.artist }}</p> 
        <p class="title">{{ album.title }}</p>
    </div>
</template>

<script>
export default {   
    props: {
        album: { type: Object, required: true },
        veto: Boolean,
        showTierBorder: { type: Boolean, default: false }
    },
    name: 'albumCover',
    methods: {
        emitAlbumClicked() {
            this.$emit('albumClicked', this.album);
        },
        tierClass(tier) {
            if (!tier) return '';
            return `tier-${tier.toLowerCase().replace(/\s+/g, '-')}`;
        }
    }
}

</script>

<style scoped>
.album {
  position: relative;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 180px;
  min-width: 180px;
  max-width: 180px;
  padding: 10px;
  border-radius: 10px;
  color: #fff;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  flex-shrink: 0;
}

.album::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.album:hover {
  cursor: pointer;
  opacity: 0.9;
  border-color: rgba(255, 255, 255, 0.2);
  z-index: 10;
}

.album:hover::before {
  opacity: 1;
}

.album:active {
  opacity: 0.8;
}

.album p {
  width: 100%;
  margin: 0;
  font-size: 11px; /* Increased from 10px */
  line-height: 1.3;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.album .artist {
  font-weight: 700;
  margin-top: 6px;
  color: #f8fafc;
  font-size: 12px; /* Increased from 11px */
}

.album .title {
  font-weight: 400;
  margin-top: 2px;
  opacity: 0.9;
  color: #e2e8f0;
  font-size: 11px; /* Increased from 10px */
}

.album img {
  width: 140px;
  height: 140px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  margin-bottom: 8px;
}

.album:hover img {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  /* Remove image scaling to prevent clipping */
}

.veto-warning-icon {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 20px;
  background: rgba(255, 0, 0, 0.9);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
  z-index: 10;
}

.test-album-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  font-size: 16px;
  background: rgba(0, 188, 212, 0.9);
  border-radius: 4px;
  padding: 2px 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
  z-index: 10;
  color: white;
  font-weight: 600;
  border: 1px solid rgba(0, 188, 212, 0.5);
  box-shadow: 0 2px 8px rgba(0, 188, 212, 0.3);
}

/* Enhanced tier-based styles using CSS custom properties */
.tier-s { 
  background: linear-gradient(135deg, var(--tier-s-alpha) 0%, rgba(240, 0, 0, 0.06) 100%); 
  border: 1.5px solid var(--tier-s-color); 
  box-shadow: 0 0 20px var(--tier-s-glow);
}

.tier-a { 
  background: linear-gradient(135deg, var(--tier-a-alpha) 0%, rgba(255, 125, 0, 0.06) 100%); 
  border: 1.5px solid var(--tier-a-color); 
  box-shadow: 0 0 20px var(--tier-a-glow);
}

.tier-b { 
  background: linear-gradient(135deg, var(--tier-b-alpha) 0%, rgba(255, 233, 2, 0.06) 100%); 
  border: 1.5px solid var(--tier-b-color); 
  box-shadow: 0 0 20px var(--tier-b-glow);
}

.tier-c { 
  background: linear-gradient(135deg, var(--tier-c-alpha) 0%, rgba(123, 192, 67, 0.06) 100%); 
  border: 1.5px solid var(--tier-c-color); 
  box-shadow: 0 0 20px var(--tier-c-glow);
}

.tier-d { 
  background: linear-gradient(135deg, var(--tier-d-alpha) 0%, rgba(31, 179, 211, 0.06) 100%); 
  border: 1.5px solid var(--tier-d-color); 
  box-shadow: 0 0 20px var(--tier-d-glow);
}

.tier-e { 
  background: linear-gradient(135deg, var(--tier-e-alpha) 0%, rgba(93, 115, 126, 0.06) 100%); 
  border: 1.5px solid var(--tier-e-color); 
  box-shadow: 0 0 20px var(--tier-e-glow);
}

.tier-not-rated-yet { 
  background: linear-gradient(135deg, var(--tier-not-rated-alpha) 0%, rgba(156, 163, 175, 0.06) 100%); 
  border: 1.5px solid var(--tier-not-rated-color); 
  box-shadow: 0 0 20px var(--tier-not-rated-glow);
}

.tier-now-listening-to { 
  background: linear-gradient(135deg, var(--tier-now-listening-alpha) 0%, rgba(139, 92, 246, 0.06) 100%); 
  border: 1.5px solid var(--tier-now-listening-color); 
  box-shadow: 0 0 20px var(--tier-now-listening-glow);
}

/* Legacy tier names for backward compatibility */
.tier-f { 
  background: linear-gradient(135deg, var(--tier-e-alpha) 0%, rgba(93, 115, 126, 0.06) 100%); 
  border: 1.5px solid var(--tier-e-color); 
  box-shadow: 0 0 20px var(--tier-e-glow);
}

/* Simplified hover effects for performance */
.tier-s:hover { 
  border-color: var(--tier-s-color); 
  opacity: 0.9;
}
.tier-a:hover { 
  border-color: var(--tier-a-color); 
  opacity: 0.9;
}
.tier-b:hover { 
  border-color: var(--tier-b-color); 
  opacity: 0.9;
}
.tier-c:hover { 
  border-color: var(--tier-c-color); 
  opacity: 0.9;
}
.tier-d:hover { 
  border-color: var(--tier-d-color); 
  opacity: 0.9;
}
.tier-e:hover { 
  border-color: var(--tier-e-color); 
  opacity: 0.9;
}
.tier-not-rated-yet:hover { 
  border-color: var(--tier-not-rated-color); 
  opacity: 0.9;
}
.tier-now-listening-to:hover { 
  border-color: var(--tier-now-listening-color); 
  opacity: 0.9;
}
.tier-f:hover { 
  border-color: var(--tier-e-color); 
  opacity: 0.9;
}
</style>